import { Close } from '@mui/icons-material';
import {
	Alert,
	Drawer,
	Grid2,
	IconButton,
	Stack,
	Typography
} from '@mui/material';
import React from 'react';
import { AUDIT_DETAIL_KEYS, IAuditEvent } from 'types/api';
import { getAuditEventLabel } from 'utils/auditUtils';
import AuditDetailBlock from './AuditDetailBlock';
import { AuditStatusChip, AuditTestChip } from './AuditEventChips';

interface IAuditEventDrawerProps {
	event: IAuditEvent | null;
	open: boolean;
	onClose: () => void;
}

const AuditEventFields: React.FC<{ event: IAuditEvent }> = ({ event }) => {
	const fields = [
		['time', intl.tr('audit.event.time', { time: event.occurred })],
		['user', event.loginIds?.join(', ')],
		['userId', event.userId],
		['method', event.method],
		['remoteAddress', event.remoteAddress],
		['id', event.id]
	].filter(([, value]) => !!value);

	return (
		<Grid2 container spacing={1}>
			{fields.map(([field, value]) => (
				<React.Fragment key={field}>
					<Grid2 size={4}>
						<Typography variant="body2" color="text.secondary">
							{intl.tr('audit.drawer.field', { field })}
						</Typography>
					</Grid2>
					<Grid2 size={8}>
						<Typography
							variant="body2"
							sx={{ wordBreak: 'break-all' }}
							data-testid={`audit-field-${field}`}
						>
							{value}
						</Typography>
					</Grid2>
				</React.Fragment>
			))}
		</Grid2>
	);
};

const AuditEventDrawer: React.FC<IAuditEventDrawerProps> = ({
	event,
	open,
	onClose
}) => {
	const details = AUDIT_DETAIL_KEYS.filter((key) => !!event?.details?.[key]);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}
		>
			{event && (
				// Keyed by event so expanded blocks and copy state do not carry over to the next one.
				<Stack key={event.id} gap={3} p={3} data-testid="audit-event-drawer">
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={2}
					>
						<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
							<Typography variant="h3">{getAuditEventLabel(event)}</Typography>
							<AuditStatusChip event={event} />
							<AuditTestChip event={event} />
						</Stack>
						<IconButton
							aria-label={intl.tr('general.close')}
							data-testid="audit-drawer-close"
							onClick={onClose}
						>
							<Close />
						</IconButton>
					</Stack>
					{event.errorMessage && (
						<Alert
							severity="error"
							variant="outlined"
							data-testid="audit-event-error"
							sx={{ wordBreak: 'break-word' }}
						>
							{event.errorMessage}
						</Alert>
					)}
					<AuditEventFields event={event} />
					{details.map((key, index) => (
						<AuditDetailBlock
							key={key}
							name={key}
							value={event.details![key]!}
							defaultExpanded={index === 0}
						/>
					))}
				</Stack>
			)}
		</Drawer>
	);
};

export default AuditEventDrawer;
